import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Container, Grid, useTheme } from "@mui/material";
import moment from "moment";
import { Navigate, useParams } from "react-router-dom";
import useWebSocket, { ReadyState } from "react-use-websocket";
import BitaBreadcrumbs from "../../components/Breadcrumbs";
import Chart from "../../components/Chart";
import DateTimePicker from "../../components/DateTimePicker";
import IndexNestedTables from "../../components/IndexNestedTables";
import Material from "../../components/Material";
import PageHero from "../../components/PageHero";
import RadioButtons from "../../components/RadioButtons";
import ResponsiveTable from "../../components/ResponsiveTable";
import Table from "../../components/Table";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { useIsMobileView } from "../../hooks/useWindowSize";
import { getAnalyzerToken } from "../../redux/slices/auth";
import {
  fetchIndexAssetClassOptions,
  fetchIndexesEOD,
  fetchIndexesWithWeight,
  fetchIndexHistory,
  fetchIndexList,
  fetchIndexTypes,
  fetchIndexValues,
  fetchIndexValuesFirstSecond,
  selectAssetClassOptions,
  selectIndexEod,
  selectIndexes,
  selectIndexHistory,
  selectIndexList,
  selectIndexLoading,
  selectIndexTypeOptions,
  selectIndexValues,
} from "../../redux/slices/indexes";
import { socketUrl, getV2IndexFile } from "../../services";
import { IndexEodItem, IndexFile, IndexHistoryItem } from "../../types/indexes";
import { BreadcrumbsItem, IRadioItem } from "../../types/ui";
import { formatAsTwoDecimals } from "../../utils/numeric";
import { DATE_FORMAT } from "../../utils/string";
import { useStyles } from "./styles";
import {
  indexTableCols,
  ORDERED_INDEX_FILE_TYPES,
  RadioButtonItems,
  top10ConstituentsCols,
} from "./table";

const IndexDetailsPage = () => {
  const theme = useTheme();
  const classes = useStyles();
  const params = useParams();
  const dispatch = useAppDispatch();

  const [indexFiles, setIndexFiles] = useState<IndexFile[]>([]);
  const indexes = useAppSelector(selectIndexes);
  const eod = useAppSelector(selectIndexEod);
  const indexHistory = useAppSelector(selectIndexHistory);
  const assetClassOptions = useAppSelector(selectAssetClassOptions);
  const indexTypeOptions = useAppSelector(selectIndexTypeOptions);
  const indexValues = useAppSelector(selectIndexValues);
  const indexList = useAppSelector(selectIndexList);
  const isLoading = useAppSelector(selectIndexLoading);

  const indexValuesFirstSecondAll = useAppSelector(
    (state) => state.indexes.indexValuesFirstSecond
  );
  const [period, setPeriod] = useState<string | number>(-10);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const [filteredEod, setFilteredEod] = useState<IndexEodItem[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<IndexHistoryItem[]>(
    []
  );

  const inMobile = useIsMobileView();

  const { lastMessage, readyState, sendJsonMessage } = useWebSocket(socketUrl);

  // const connectionStatus = {
  //   [ReadyState.CONNECTING]: "Connecting",
  //   [ReadyState.OPEN]: "Open",
  //   [ReadyState.CLOSING]: "Closing",
  //   [ReadyState.CLOSED]: "Closed",
  //   [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  // }[readyState];

  const constituentsDownloadDisabledIndex = ['BCAUEE','BIF1US'];
  const constituentsListDisabledIndex = ['BCAUEE','BIF1US'];

  const indexListItem = useMemo(
    () => indexList?.find((item) => item.ticker === params.id),
    [indexList, params.id]
  );

  const websocketId = indexListItem?.internal_ticker || params.id;

  const index = useMemo(() => {
    if (!indexes?.length) return undefined;
    return indexes.find((i) => i.index_id === params.id);
  }, [indexes, params.id]);

  const indexValue = useMemo(() => {
    if (!indexValues?.length) return undefined;
    return indexValues.find((i) => i.index_id === params.id);
  }, [indexValues, params.id]);

  const indexValueFirstSecond = useMemo(() => {
    if (!indexValuesFirstSecondAll?.length) return undefined;
    return indexValuesFirstSecondAll.find((v) => v.symbol === params.id);
  }, [indexValuesFirstSecondAll, params.id]);

  const indexHistoryItem = useMemo(() => {
    return indexHistory?.find(({ index_id }) => index_id === params.id);
  }, [indexHistory, params.id]);

  const lastIndexValue = useMemo(() => {
    try {
      const data = JSON.parse(lastMessage?.data ?? "{}");

      // Pick only correct data
      if (
        data.channel === "live" &&
        data.event === "update" &&
        data.id === websocketId &&
        (data?.data?.index_value || data?.data?.price)
      ) {
        return data?.data?.index_value || data.data.price;
      }
      return indexValue?.value ?? 0;
    } catch (error) {
      return 0;
    }
  }, [indexValue?.value, lastMessage?.data, websocketId]);

  const filesToShow = useMemo(() => {
    const clonedIndexFiles: IndexFile[] = [...indexFiles];
    const constituentsIndex = clonedIndexFiles.findIndex(
      (file) => file.type === "constituents_list"
    );

    if (
      constituentsIndex < 0 &&
      index?.n_constituents &&
      parseInt(index?.n_constituents.toString()) > 1 &&
      !constituentsDownloadDisabledIndex.includes(index.index_id)
    ) {
      clonedIndexFiles.push({
        createdate: "",
        file: "",
        id_m_index: -1,
        id_m_index_file: -1,
        key: `Constituent_List_${index.index_id}.xlsx`,
        status: "A",
        type: "constituents_list",
      });
    }

    if (
      constituentsIndex >= 0 &&
      index?.n_constituents &&
      index?.n_constituents === 1
    ) {
      clonedIndexFiles.splice(constituentsIndex, 1);
    }

    return clonedIndexFiles.sort(
      (a, b) =>
        ORDERED_INDEX_FILE_TYPES.findIndex((type) => type === a.type) -
        ORDERED_INDEX_FILE_TYPES.findIndex((type) => type === b.type)
    );
  }, [index?.index_id, index?.n_constituents, indexFiles]);

  const indexData = useMemo(() => {
    const index_id: string = index?.index_id || "";
    const last_eod_value: number = eod[index_id]?.[eod[index_id]?.length - 1]?.value || 0;
    const penultimate_eod_value: number = eod[index_id]?.[eod[index_id]?.length - 2]?.value || 0;
    const index_value = indexValueFirstSecond?.index_value;

    const calculateValueDiff = (): number => {
      let valueDiff = 0;
      if (lastIndexValue && index_value) {
        valueDiff = formatAsTwoDecimals(lastIndexValue - index_value);
      }
      if (valueDiff === 0) {
        valueDiff = last_eod_value - penultimate_eod_value;
      }
      return valueDiff;
    };

    const calculateLastDayReturn = (): number => {
      let lastDayReturn = 0;
      if (lastIndexValue && index_value) {
        lastDayReturn = formatAsTwoDecimals(((lastIndexValue - index_value) / lastIndexValue) * 100);
      }
      if (lastDayReturn === 0 && index_value) {
        lastDayReturn = ((last_eod_value - penultimate_eod_value) / index_value) * 100;
      }
      return lastDayReturn;
    };

    const valueDiff = calculateValueDiff();
    const lastDayReturn = calculateLastDayReturn();

    return {
      ...index,
      value: (lastIndexValue || 0).toFixed(2),
      last_day_return: `${valueDiff.toFixed(2)} (${lastDayReturn.toFixed(
        2
      )}%)`,
      valueDiff,
    };
  }, [index, indexValueFirstSecond?.index_value, lastIndexValue, eod]);

  const indexColumns = useMemo(
    () =>
      // Hide any empty columns out
      indexTableCols.filter(
        (col) =>
          !!(indexData as any)[col.key] || (indexData as any)[col.key] === 0
      ),
    [indexData]
  );

  const radioItems: IRadioItem[] = useMemo(() => {
    if (!params.id || !eod?.[params.id]) return RadioButtonItems;

    return RadioButtonItems.map((item, idx) => {
      if (idx === 0 || idx === RadioButtonItems.length - 1) {
        return item;
      }

      // Mark periods that don't have relevant data as "disabled"
      return {
        ...item,
        disabled:
          moment()
            .add(item.value, "years")
            .diff(moment(eod[params.id ?? ""][0]?.timestamp)) < 0,
      };
    });
  }, [eod, params.id]);

  const disabledItemIds = useMemo(
    () => radioItems.filter((item) => !!item.disabled).map((item) => item.key),
    [radioItems]
  );

  const chartData = useMemo(() => {
    // Try to get chart data from EOD first
    if (filteredEod.length) {
      return filteredEod.map((item) => {
        const { timestamp, value } = item;
        return [moment(timestamp).valueOf(), value]; // Need to convert date string to timestamp: required by Highcharts
      });
    }

    // Some indexes have history instead of EOD
    if (filteredHistory.length) {
      return filteredHistory.map((item) => {
        const { timestamp_real, close } = item;
        return [moment(timestamp_real).valueOf(), close]; // Need to convert date string to timestamp: required by Highcharts
      });
    }

    return [];
  }, [filteredEod, filteredHistory]);

  const assetClass = useMemo(
    () =>
      assetClassOptions.find((aco) => aco.id === indexListItem?.asset_class),
    [assetClassOptions, indexListItem?.asset_class]
  );

  const indexType = useMemo(
    () => indexTypeOptions.find((ito) => ito.id === indexListItem?.index_type),
    [indexListItem?.index_type, indexTypeOptions]
  );

  const breadcrumbsItems: BreadcrumbsItem[] = useMemo(() => {
    if (!assetClass || !indexType || !index?.index_id) return [];
    return [
      {
        key: 0,
        label: "BITA Indexes",
        to: "/index",
      },
      {
        key: 0,
        label: assetClass.name,
        to: `/index?asset_class=${assetClass.id}`,
      },
      {
        key: 0,
        label: indexType.name,
        to: `/index?asset_class=${assetClass.id}&index_type=${indexType.id}`,
      },
      {
        key: 0,
        label: index.index_id,
        to: `/index/${index.index_id}`,
      },
    ];
  }, [assetClass, index?.index_id, indexType]);

  const fetchIndexFiles = useCallback(async () => {
    try {
      if (indexListItem?.index_id) {
        const { data } = await getV2IndexFile(indexListItem.index_id);
        if (data) {
          setIndexFiles(data);
        }
      }
    } catch (error) {}
  }, [indexListItem?.index_id]);

  useEffect(() => {
    if (readyState === ReadyState.OPEN && websocketId) {
      // Subscribe to index channel
      sendJsonMessage({
        event: "subscribe",
        id: websocketId,
        channel: "live",
      });
    }
  }, [readyState, sendJsonMessage, websocketId]);

  useEffect(() => {
    if (!index) {
      dispatch(fetchIndexesWithWeight());
    }
  }, [dispatch, index]);

  useEffect(() => {
    dispatch(fetchIndexValuesFirstSecond());
  }, [dispatch]);

  useEffect(() => {
    if (!params.id) return;
    const ids = [params.id];
    const startDate = moment().add(-10, "years").format(DATE_FORMAT); // 10 years ago
    const endDate = moment().format(DATE_FORMAT); // Today

    dispatch(
      fetchIndexesEOD({
        indexes: ids,
        startDate,
        endDate,
      })
    );
    dispatch(
      fetchIndexValues({
        indexes: ids,
      })
    );
  }, [dispatch, params.id]);

  useEffect(() => {
    // Only if there are no EOD items, try to fetch index history items
    if (params.id && eod?.[params.id] && eod[params.id].length === 0) {
      const startDate = moment().add(-10, "years").format(DATE_FORMAT); // 10 years ago
      const endDate = moment().format(DATE_FORMAT); // Today

      dispatch(
        fetchIndexHistory({
          indexId: params.id,
          startDate,
          endDate,
          frequency: "1d",
        })
      );
    }
  }, [dispatch, eod, params.id]);

  // Filter items by years selector
  useEffect(() => {
    if (!params.id || !eod || !eod[params.id]) {
      setFilteredEod([]);
      return;
    }

    const minDate =
      period === "YTD"
        ? moment().startOf("year")
        : moment().add(period, "years");

    setFilteredEod(
      eod[params.id].filter((item) => {
        return moment(item.timestamp).diff(minDate, "days") >= 0;
      })
    );
  }, [eod, params.id, period]);

  useEffect(() => {
    if (!params.id || !indexHistoryItem || !indexHistoryItem.data?.length) {
      setFilteredHistory([]);
      return;
    }

    const minDate =
      period === "YTD"
        ? moment().startOf("year")
        : moment().add(period, "years");

    setFilteredHistory(
      indexHistoryItem.data.filter(
        (item) => moment(item.timestamp_real).diff(minDate, "days") >= 0
      )
    );
  }, [indexHistoryItem, params.id, period]);

  // Filter items by date range selected
  useEffect(() => {
    if (!from || !to) return;
    if (!params.id || !eod || !eod[params.id]) {
      setFilteredEod([]);
      return;
    }

    setFilteredEod(
      eod[params.id].filter((item) => {
        return (
          moment(item.timestamp).diff(moment(from), "days") >= 0 &&
          moment(item.timestamp).diff(moment(to), "days") <= 0
        );
      })
    );
  }, [eod, from, params.id, to]);

  useEffect(() => {
    if (!from || !to) return;

    if (!params.id || !indexHistoryItem || !indexHistoryItem.data?.length) {
      setFilteredHistory([]);
      return;
    }

    setFilteredHistory(
      indexHistoryItem.data.filter(
        (item) =>
          moment(item.timestamp_real).diff(moment(from), "days") >= 0 &&
          moment(item.timestamp_real).diff(moment(to), "days") <= 0
      )
    );
  }, [from, indexHistoryItem, params.id, to]);

  useEffect(() => {
    dispatch(fetchIndexList());
    dispatch(fetchIndexAssetClassOptions());
    dispatch(fetchIndexTypes());
  }, [dispatch]);

  useEffect(() => {
    fetchIndexFiles();
  }, [fetchIndexFiles]);

  useEffect(() => {
    // Get analyzer api token, which is required in future api requests
    dispatch(
      getAnalyzerToken({
        username: process.env.REACT_APP_ANALYZER_API_USER ?? "",
        password: process.env.REACT_APP_ANALYZER_API_PWD ?? "",
      })
    );
  }, [dispatch]);

  if (!indexes?.length) return null;
  if (index?.web_status !== "A" && index?.web_status !== "H")
    return <Navigate to="/" />;

  return (
    <Box className={classes.root}>
      <PageHero
        colorScheme="dark_blue"
        title={index.name}
        subTitle={index.index_id}
        responsiveHeading
      />

      <Container className={classes.main}>
        <Box pb={4}>
          <BitaBreadcrumbs items={breadcrumbsItems} />
        </Box>
        <Box className={classes.mainHeading}>Index Description</Box>
        <Box
          className={`${classes.mainText} ${classes.withBorderBottom}`}
          sx={{ color: "text.secondary" }}
        >
          {index.description}
        </Box>
        <Box className={`${classes.tableContainer}`}>
          {params.id && eod?.[params.id] ? (
            <Box>
              <Box className={classes.mainHeading}>Performance</Box>
              <Box className={classes.selectorsContainer}>
                <RadioButtons
                  items={radioItems}
                  currentValue={period}
                  onChange={setPeriod}
                />
                <Box className={classes.datePickersContainer}>
                  <DateTimePicker
                    className={classes.mobileDatePicker}
                    showTime={false}
                    label="From"
                    currentValue={from}
                    onChange={(val) => setFrom(val)}
                  />
                  <DateTimePicker
                    showTime={false}
                    label="To"
                    currentValue={to}
                    onChange={(val) => setTo(val)}
                  />
                </Box>
              </Box>
              <Box>
                <Chart
                  loading={isLoading && !chartData?.length}
                  dataSeries={[
                    {
                      type: "area",
                      name: params.id,
                      data: chartData,
                      color: theme.palette.primary.dark,
                      lineColor: theme.palette.primary.main,
                    },
                  ]}
                />
              </Box>
            </Box>
          ) : null}

          <Box className={`${classes.withBorderBottom}`}>
            <ResponsiveTable
              inMobile={inMobile}
              data={indexData}
              columns={indexColumns}
            />

            <IndexNestedTables
              id={params.id ?? ""}
              disabledItemIds={disabledItemIds}
            />
          </Box>

          <Box className={`${classes.withBorderBottom}`} mt={4} pb={4}>
            <Grid container spacing={4}>
              {index.components?.length && !constituentsListDisabledIndex.includes(index.index_id) ? (
                <Grid item xs={12} sm={6}>
                  <Box className={classes.sectionHeading} sx={{ mb: "1rem" }}>
                    Top 10 Constituents
                  </Box>
                  <Table
                    columns={top10ConstituentsCols}
                    data={index.components}
                    dense
                    hasPagination={false}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6}>
                <Box className={classes.sectionHeading}>Download Materials</Box>
                <Grid container spacing={2} mt={0}>
                  {filesToShow.map((file) => (
                    <Grid key={file.id_m_index_file} item xs={12} sm={6}>
                      <Material
                        type={file.type}
                        url={file.file}
                        fileIndex={file.id_m_index_file}
                        indexId={index.index_id}
                        fileName={file.key}
                        background="linear-gradient(360deg, #005FEE 0%, #0031D9 100%)"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default IndexDetailsPage;
